// First-run setup for self-signup orgs: the pure rules behind
// components/FirstRun. Backend: GET /api/v1/me/onboarding and
// POST /api/v1/me/onboarding/first_run (combird_apps FirstRunService).
import {isAdminPanelRole} from '../auth/roles';

export const CHOICE_DLT_READY = 'FIRST_RUN_CHOICE_DLT_READY';
export const CHOICE_DLT_PENDING = 'FIRST_RUN_CHOICE_DLT_PENDING';

// Same rules as the backend (first_run.validateChoice).
const PE_ID_RE = /^[0-9]{19}$/;
const SENDER_ID_RE = /^[A-Z0-9]{3,11}$/;
export const MAX_SENDER_IDS = 5;

// firstRunView says what to put in front of the app:
//   'none'       — nothing (not a self-signup, already set up, or platform staff)
//   'setup'      — the blocking setup dialog (org admin)
//   'wait_admin' — a blocking "your admin must finish setup" notice (other members)
export const firstRunView = (onboarding, {roles = [], role = '', impersonating = false} = {}) => {
  if (!onboarding || !onboarding.showFirstRun) {
    return 'none';
  }
  if (impersonating || isAdminPanelRole(role)) {
    return 'none';
  }
  return roles.includes('org_admin') ? 'setup' : 'wait_admin';
};

// initialStep resumes where the admin left off.
export const initialStep = (onboarding) => {
  switch (onboarding && onboarding.firstRunChoice) {
  case CHOICE_DLT_PENDING:
    return 'no_dlt';
  case CHOICE_DLT_READY:
    return 'dlt_form';
  default:
    return 'choose';
  }
};

// "acmein, ACMEOT acmein" → ['ACMEIN', 'ACMEOT']
export const parseSenderIds = (text) => {
  const seen = [];
  String(text || '').split(/[\s,]+/).forEach((raw) => {
    const id = raw.trim().toUpperCase();
    if (id && !seen.includes(id)) {
      seen.push(id);
    }
  });
  return seen;
};

// PE IDs that are not a customer's own registration: the shared placeholder
// some tenants carry (see AdminPanel/orgSearch) and the example in the form.
const PLACEHOLDER_PE_IDS = ['1234567890123456789', '1201234567890123456'];

// normalizePeId drops spaces a copy from the DLT portal can carry.
export const normalizePeId = (raw) => String(raw || '').replace(/\s+/g, '');

// validatePeId returns an error message, or '' for a valid PE ID. Same rule
// as the backend (19 digits), plus the obvious non-registrations.
export const validatePeId = (raw) => {
  const peId = normalizePeId(raw);
  if (!peId) {
    return 'Enter your Principal Entity (PE) ID.';
  }
  if (!/^[0-9]+$/.test(peId)) {
    return 'The PE ID has digits only. Check the value you copied from your DLT portal.';
  }
  if (!PE_ID_RE.test(peId)) {
    return `The PE ID is 19 digits; this one has ${peId.length}.`;
  }
  if (PLACEHOLDER_PE_IDS.includes(peId) || /^(\d)\1+$/.test(peId)) {
    return 'Enter your own PE ID from your DLT portal.';
  }
  return '';
};

// validateSenderIds returns an error message naming the first bad header, or
// '' when the list can be sent. Same rules as the backend and the SMS send
// path: 3 to 11 letters or digits, upper-cased, at most MAX_SENDER_IDS.
export const validateSenderIds = (senderIds) => {
  if (senderIds.length === 0) {
    return 'Enter at least one approved header (sender ID).';
  }
  if (senderIds.length > MAX_SENDER_IDS) {
    return `Enter at most ${MAX_SENDER_IDS} headers here (you entered ${senderIds.length}); you can add more later.`;
  }
  const badChars = senderIds.find((id) => !/^[A-Z0-9]+$/.test(id));
  if (badChars) {
    return `"${badChars}": a header has letters and digits only, e.g. ACMEIN.`;
  }
  const badLength = senderIds.find((id) => !SENDER_ID_RE.test(id));
  if (badLength) {
    return `"${badLength}": a header is 3 to 11 characters; this one has ${badLength.length}.`;
  }
  return '';
};

// validateDltForm returns {field: message}; empty when the form can be sent.
// Templates are not asked at first run; they are added from Templates/Headers.
export const validateDltForm = ({peId, senderIds}) => {
  const errors = {};
  const peIdError = validatePeId(peId);
  if (peIdError) {
    errors.peId = peIdError;
  }
  const senderIdsError = validateSenderIds(senderIds);
  if (senderIdsError) {
    errors.senderIds = senderIdsError;
  }
  return errors;
};

// errorReason is the backend's ErrorInfo reason (e.g. FIRST_RUN_INVALID_DLT_DETAILS)
// from an Api rejection, or ''.
export const errorReason = (err) => {
  const details = err && err.response && err.response.data && err.response.data.details;
  const info = Array.isArray(details) && details.find((d) => d && String(d['@type'] || '').includes('ErrorInfo'));
  return (info && info.reason) || '';
};

// errorMessage turns an Api rejection into one line for the user.
export const errorMessage = (err) => {
  const data = (err && err.response && err.response.data) || {};
  return data.message || (data.err && data.err.msg) || 'Something went wrong. Please try again.';
};
